
import { get, set } from "../utils/tool";
import type { MonitorSdk, MonitorPlugin, MonitoringSendData } from "../types/type";
import { createFingerprint } from '../utils/fingerprinting';
import { generateRandomID } from "../utils/tool";
// import { UAParser } from 'ua-parser-js';
// const UAParser = (_args: any) => ({ browser: { name: '', version: '' }, os: { name: '', version: '' } });

type SysInfo = {
  browser: {
    name: string;
    version: string;
  }
  os: {
    name: string;
    version: string;
  }
}
export default class BrowserBase implements MonitorPlugin {
  endpoint: string = '';
  sdk?: MonitorSdk;
  sysInfo: SysInfo | null = null;

  init(sdk: MonitorSdk): void {
    this.sdk = sdk;

    sdk.send = this.send.bind(this);

    sdk.setTraceId(this.getTraceId());
    sdk.setSpanId(this.getSpanId());

    sdk.startFlushTimer();

    document.addEventListener("visibilitychange", function logData() {
      if (document.visibilityState === "hidden") {
        sdk.flush();
      }
    });

    if (typeof fetch === 'function') {
      fetch('https://static.hopegoo.com/aux/getip', {
        method: 'GET',
        mode: 'cors',
        headers: {
          'Content-Type': 'application/json',
        },
      }).then((response) => {
        return response.json();
      }).then((data) => {
        // const ip = get(data, 'ip');
        const countryCode = get(data, 'country.iso_code');
        if (countryCode) {
          set(sdk, 'location.district', countryCode);
        }
      })

      fetch('https://static.hopegoo.com/aux/getua', {
        method: 'GET',
        mode: 'cors',
        headers: {
          'Content-Type': 'application/json',
        },
      }).then((response) => {
        return response.json();
      }).then((data) => {
        this.sysInfo = data;
      })
    }

  }


  send(dataToSend: MonitoringSendData[]) {
    const endpoint = this.sdk?.getEndpoint();
    if (!endpoint) return

    const xhr = new XMLHttpRequest();
    xhr.open("POST", endpoint, true);
    xhr.setRequestHeader("Content-Type", "application/json");

    xhr.onreadystatechange = () => {
      if (xhr.readyState === 4) {
        if (xhr.status >= 200 && xhr.status < 300) {
          // console.log("Monitoring data reported successfully");
        } else {
          console.error("Failed to report monitoring data:", xhr.statusText);
        }
      }
    };
    xhr.onerror = () => {
      console.error("Error reporting monitoring data:", xhr.statusText);
    };

    xhr.send(JSON.stringify({ data: dataToSend }));
  }

  getSysInfo() {
    if (this.sysInfo) return this.sysInfo;
    const { osName, osVersion } = this.getOSInfo();
    const { browserName, fullVersion } = this.getBrowserInfo();
    return {
      os: {
        name: osName,
        version: osVersion,
      },
      browser: {
        name: browserName,
        version: fullVersion,
      },
    };
  }

  addBaseInfo() {
    const connection = (navigator as any).connection || (navigator as any).mozConnection || (navigator as any).webkitConnection || {};

    let deviceId: string = this.sdk?.deviceId || localStorage.getItem('__CLIENT_INFO_DEVICE_ID__') || '';

    if (!deviceId) {
      deviceId = String(createFingerprint());
      if (deviceId) localStorage.setItem('__CLIENT_INFO_DEVICE_ID__', deviceId);
    }

    const page = window.location.href.split('?')[0];

    if (this.sdk && deviceId) {
      set(this.sdk, 'user.deviceId', deviceId);
    }

    const { browser, os } = this.getSysInfo();

    const sys = `${os.name}/${os.version}, ${browser.name}/${browser.version}`;

    return {
      network: connection.effectiveType,
      page: encodeURI(page),
      sys,
    }
  }

  getOSInfo() {
    const userAgent = navigator.userAgent;
    let osName = "Unknown OS";
    let osVersion = "Unknown Version";

    if (userAgent.indexOf("Win") !== -1) {
      osName = "Windows";
      const ntVersionMatch = userAgent.match(/Windows NT (\d+\.\d+)/);
      if (ntVersionMatch) {
        osVersion = 'NT' + ntVersionMatch[1];
      }
    } else if (userAgent.indexOf("Mac") !== -1 && userAgent.indexOf("iPhone") === -1 && userAgent.indexOf("iPad") === -1 && userAgent.indexOf("iPod") === -1) {
      osName = "MacOs";
      osVersion = userAgent.substring(userAgent.indexOf("Mac OS X") + 9).split(")")[0].replace(/_/g, ".");
    } else if (userAgent.indexOf("iPhone") !== -1 || userAgent.indexOf("iPad") !== -1 || userAgent.indexOf("iPod") !== -1) {
      osName = "IOS";
      const iosVersionMatch = userAgent.match(/OS (\d+_\d+(_\d+)?)/);
      if (iosVersionMatch) {
        osVersion = iosVersionMatch[1].replace(/_/g, ".");
      }
    } else if (userAgent.indexOf("X11") !== -1) {
      osName = "UNIX";
    } else if (userAgent.indexOf("Linux") !== -1) {
      osName = "Linux";
      if (userAgent.indexOf("Android") !== -1) {
        osName = "Android";
        osVersion = userAgent.substring(userAgent.indexOf("Android") + 8).split(";")[0];
      }
    }

    return { osName, osVersion };
  }

  getBrowserInfo() {
    const userAgent = navigator.userAgent;
    let browserName = "Unknown";
    let fullVersion = "Unknown";

    if (userAgent.indexOf("Chrome") !== -1) {
      browserName = "Chrome";
      fullVersion = userAgent.substring(userAgent.indexOf("Chrome") + 7);
      fullVersion = fullVersion.split(" ")[0];
    } else if (userAgent.indexOf("Firefox") !== -1) {
      browserName = "Firefox";
      fullVersion = userAgent.substring(userAgent.indexOf("Firefox") + 8);
    } else if (userAgent.indexOf("Safari") !== -1 && userAgent.indexOf("Chrome") === -1) {
      browserName = "Safari";
      fullVersion = userAgent.substring(userAgent.indexOf("Version") + 8);
      fullVersion = fullVersion.split(" ")[0];
    } else if (userAgent.indexOf("MSIE") !== -1 || !!(document as any).documentMode) {
      browserName = "Internet Explorer";
      fullVersion = userAgent.substring(userAgent.indexOf("MSIE") + 5);
      fullVersion = fullVersion.split(";")[0];
    }

    return { browserName, fullVersion };
  }

  private getTraceId() {
    const sessionTraceId = sessionStorage.getItem('__CLIENT_INFO_TRACE_ID__');
    if (sessionTraceId) return sessionTraceId;

    const traceId = generateRandomID(32);
    sessionStorage.setItem('__CLIENT_INFO_TRACE_ID__', traceId);
    return traceId;
  }

  private getSpanId() {
    const spanId = generateRandomID(16);
    return spanId;
  }
}
